import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIllustration } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  type FileType,
  MIME_TYPE_MAPPING,
} from 'lumen/utils/constants/file-types';
import { v4 as uuidv4 } from 'uuid';

interface GeneralFileDropZoneSignature {
  Args: {
    allowedFormats?: FileType[];
    isDisabled?: boolean;
    isMultiple?: boolean;
    maxFileSize?: number; // in bytes, default is 10MB
    onFileDrop: (files: FileWithValidation[], isTooManyFiles: boolean) => void;
  };
  Element: HTMLDivElement;
}

type ValidationError = 'InvalidFileType' | 'FileTooLarge';
export type FileWithValidation = {
  file: File;
  validationErrors: ValidationError[];
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function extractFiles(event: DragEvent) {
  if (event.dataTransfer?.items) {
    return Array.from(event.dataTransfer.items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file) => file !== null);
  }

  if (event.dataTransfer?.files) {
    return Array.from(event.dataTransfer.files);
  }

  return [];
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default class GeneralFileDropZone extends Component<GeneralFileDropZoneSignature> {
  @service declare intl: Services['intl'];

  @tracked isHovering = false;

  inputId = `file-drop-zone-${uuidv4()}`;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayMaxFileSize() {
    const sizeInMB = this.maxFileSize / (1024 * 1024);
    return this.intl.formatNumber(sizeInMB, {
      style: 'unit',
      unit: 'megabyte',
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAllowedFileFormats() {
    const { allowedFormats } = this.args;
    if (!allowedFormats || allowedFormats.length === 0) {
      return null;
    }

    // if there's more than one we need to put or before the last item
    // e.g. 'jpg, png, or gif'
    if (allowedFormats.length > 1) {
      const lastFormat = allowedFormats[allowedFormats.length - 1];
      const formats = allowedFormats.slice(0, allowedFormats.length - 1);
      return `${formats.join(', ')} or ${lastFormat}`;
    }

    return allowedFormats[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get inputAcceptTypes() {
    const { mimeTypes } = this;

    if (mimeTypes === undefined) {
      return undefined;
    }

    return mimeTypes.join(',');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxFileSize() {
    return this.args.maxFileSize ?? MAX_FILE_SIZE;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mimeTypes() {
    const { allowedFormats } = this.args;

    if (!allowedFormats || allowedFormats.length === 0) {
      return undefined;
    }

    return allowedFormats
      .map((format) => MIME_TYPE_MAPPING[format])
      .filter(Boolean);
  }

  /**
   * For security reasons, we can't access the files directly from the drag event.
   * Meaning we can't do validation on the files before the drop.
   **/
  @action onDragEnter(): void {
    this.isHovering = true;
  }

  @action onDragLeave(): void {
    this.reset();
  }

  @action onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHovering = true;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onDrop(event: DragEvent) {
    event.preventDefault();

    const { isDisabled, onFileDrop } = this.args;
    this.reset();

    if (isDisabled) {
      return;
    }

    const files = extractFiles(event);
    const isTooManyFiles = this.args.isMultiple ? false : files.length > 1;

    return onFileDrop(this.validateFiles(files), isTooManyFiles);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onFileChange(event: Event) {
    const { isDisabled, onFileDrop } = this.args;
    const inputElement = event.target as HTMLInputElement;

    if (isDisabled) {
      return;
    }

    const files = Array.from(inputElement.files ?? []);
    const isTooManyFiles = this.args.isMultiple ? false : files.length > 1;

    return onFileDrop(this.validateFiles(files), isTooManyFiles);
  }

  @action reset(): void {
    this.isHovering = false;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  validateFiles(files: File[]) {
    const { mimeTypes } = this;

    return files.map<FileWithValidation>((file) => {
      const validationErrors: ValidationError[] = [];

      if (mimeTypes && !mimeTypes.includes(file.type)) {
        validationErrors.push('InvalidFileType');
      }

      if (file.size > this.maxFileSize) {
        validationErrors.push('FileTooLarge');
      }

      return { file, validationErrors };
    });
  }

  <template>
    <div
      class="rounded-200 border-general-neutral-300 relative flex flex-col items-center justify-center gap-400 border-2 border-dashed px-400 py-500
        {{if this.isHovering 'bg-neutral-subdued' ''}}"
      {{on "dragenter" this.onDragEnter}}
      {{on "dragleave" this.onDragLeave}}
      {{on "dragover" this.onDragOver}}
      {{on "drop" this.onDrop}}
      data-test-id="file-drop-zone"
      ...attributes
    >

      {{#if this.isHovering}}
        <p
          class="text-300--medium text-neutral-subdued"
          data-test-id="drop-to-unload"
        >{{t "general.drag_and_drop.drop_to_upload"}}</p>
      {{else}}
        <PrismIllustration @name="100x100_upload" @size="medium" />
        <div class="flex flex-col gap-200 text-center">
          <p class="text-300--medium" data-test-id="dropzone-title">
            <span>{{t "general.drag_and_drop.drop_file"}}</span>
            <span>{{t "general.or"}}</span>
            <label
              class="prism-link"
              data-test-id="browse-files-link"
              for={{this.inputId}}
            >{{t "general.drag_and_drop.browse"}}</label>
          </p>
          <p class="paragraph-200--regular">
            {{#if @allowedFormats}}
              <span>{{t "general.drag_and_drop.supported_file_formats"}}:</span>
              <span
                data-test-id="formatted-allowed-file-formats"
              >{{this.formattedAllowedFileFormats}}.</span>
            {{/if}}
            <span>{{t "general.drag_and_drop.max_file_size"}}:</span>
            <span
              data-test-id="display-max-file-size"
            >{{this.displayMaxFileSize}}</span>
          </p>
        </div>
        <input
          accept={{this.inputAcceptTypes}}
          class="hidden"
          data-test-id="file-input"
          id={{this.inputId}}
          multiple={{@isMultiple}}
          type="file"
          {{on "change" this.onFileChange}}
        />
      {{/if}}
    </div>
  </template>
}
